import type { PortableTextComponents } from '@portabletext/react';
import { safeImageSrcSet, safeImageUrl } from '../../lib/sanity';
import { normalizeLegacyInternalHref } from '../../lib/legacyLinks';
import { BlogFaqItem } from './BlogFaqItem';
import { BlogTableBlock } from './BlogTableBlock';
import { renderedImageDimensions, imageAspectRatio } from '../../lib/imageDimensions';

export const portableTextComponents: PortableTextComponents = {
  types: {
    image: ({ value }) => {
      const src = safeImageUrl(value, (b) => b.width(900).auto('format'));
      const srcSet = safeImageSrcSet(value, 900, (b, width) => b.width(width).auto('format'));
      if (!src) return null
      const dimensions = renderedImageDimensions(value, 900);
      const aspectRatio = imageAspectRatio(value);

      return (
        <figure className="my-8" style={aspectRatio ? { aspectRatio } : undefined}>
          <img
            src={src}
            srcSet={srcSet ?? undefined}
            alt={value.alt ?? value.caption ?? 'Blog illustration'}
            width={dimensions?.width}
            height={dimensions?.height}
            sizes="(max-width: 900px) 100vw, 900px"
            className="h-auto w-full"
          />
          {value.caption && (
            <figcaption className="mt-2 text-center text-[13px] text-on-surface-muted">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
    code: ({ value }) => (
      <pre className="my-6 overflow-x-auto rounded-lg bg-surface-container p-4 font-mono text-sm leading-relaxed text-on-surface">
        <code>{value.code}</code>
      </pre>
    ),
    faqItem: ({ value }) => (
      <BlogFaqItem question={value.question} answer={value.answer} />
    ),
    tableBlock: ({ value }) => <BlogTableBlock value={value} />,
  },
  marks: {
    link: ({ value, children }) => (
      <a
        href={normalizeLegacyInternalHref(value?.href)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-4 hover:text-[#ffcfbf]"
      >
        {children}
      </a>
    ),
    strong: ({ children }) => <strong className="blog-prose-strong">{children}</strong>,
    em: ({ children }) => <em className="blog-prose-em">{children}</em>,
  },
  block: {
    normal: ({ children }) => <p>{children}</p>,
    h2: ({ children }) => <h2>{children}</h2>,
    h3: ({ children }) => <h3>{children}</h3>,
    blockquote: ({ children }) => <blockquote>{children}</blockquote>,
  },
  list: {
    bullet: ({ children }) => (
      <ul className="blog-list blog-list--bullet">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="blog-list blog-list--number">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="blog-list-item">{children}</li>,
    number: ({ children }) => <li className="blog-list-item">{children}</li>,
  },
};
